import React, { useState, useEffect } from 'react';
import { Navbar, Container, Form, Image, Nav, OverlayTrigger, Popover, Badge, ListGroup, Button, Dropdown } from 'react-bootstrap';
import { Search, Bell, MapPin, Inbox, Sun, Moon, CircleUserRound, Sparkles, Users, X, Shield } from 'lucide-react';
import { ToastContainer, toast as Toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Logo from '../assets/images/logo.png'; 
import LogoLight from '../assets/images/logo_light.png';
import './AppNav.css';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    signOutUser as endSession, subscribeNotifications, markNotificationSeen, updateNotification, deleteNotification, acceptConnection, removeConnection,
} from '@wave-circuit/core';
import { useTheme } from '../app/ThemeProvider';
import { useAuth } from '../app/AuthContext';

function AppHeader({ searchPlaceholder, searchValue, onSearchChange, onSearchSubmit, defaultSearchOpen }) {
    const [searchOpen, setSearchOpen] = useState(defaultSearchOpen || false);
    const [searchQuery, setSearchQuery] = useState('');
    const Navigate = useNavigate();
    const location = useLocation();
    const { theme, toggleTheme } = useTheme();    
    const { profile, profileId, isAdmin, isMember } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unseenCount, setUnseenCount] = useState(0);

    const profilePhoto = profile?.photoURL || null;
    const accountType = profile?.type || profile?.accountType || null;
    // Display companyName for business profiles, fallback to username
    const username = profile?.type === 'business' && profile?.companyName
        ? profile.companyName
        : profile?.username || null;

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const routeQuery = location.pathname === '/search' ? (params.get('q') || '') : '';

        if (searchValue === undefined) {
            setSearchQuery(routeQuery);
        }

        setSearchOpen(location.pathname === '/search' || defaultSearchOpen);
    }, [location.pathname, location.search, searchValue, defaultSearchOpen]);



    useEffect(() => {
        if (!profileId) return;
        return subscribeNotifications(profileId, (notifs) => {
            setNotifications(notifs);
            setUnseenCount(notifs.filter(n => !n.seen).length);
        }, (err) => {
            console.error('Notifications listener error', err);
        });
    }, [profileId]);

    const handleNotificationsClick = async () => {
        // Mark unseen notifications as seen when opening the popover
        const unseen = notifications.filter(n => !n.seen);
        for (const n of unseen) {
            try {
                await markNotificationSeen(n.id);
            } catch (err) {
                console.error('Failed to mark notification seen', err);
            }
        }
    };

    const handleAcceptConnection = async (notification) => {
        if (!notification.connectionId) return;
        try {
            await acceptConnection(notification.connectionId);
            await updateNotification(notification.id, { message: 'Connection accepted', type: 'notice' });
        } catch (err) {
            console.error('Failed to accept connection', err);
        }
    };

    const handleRejectConnection = async (notification) => {
        if (!notification.connectionId) return;
        try {
            await removeConnection(notification.connectionId);
            await deleteNotification(notification.id);
        } catch (err) {
            console.error('Failed to reject connection', err);
        }
    };

    const signOutUser = () => {
        endSession().then(() => {
            Toast('You have been logged out', {position: 'top-center', autoClose: 3000});
            Navigate('/login');
        }).catch((error) => {
            // Optionally handle error
        });
    }

    const handleThemeToggle = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        toggleTheme();
        Toast(`${next.charAt(0).toUpperCase() + next.slice(1)} mode enabled`, {position: 'top-center', autoClose: 2000});
    };

    const submitSearch = (queryValue) => {
        // Fallback to internal searchQuery if queryValue isn't provided (like in old direct calls)
        const activeQuery = queryValue !== undefined ? queryValue : (searchValue !== undefined ? searchValue : searchQuery);
        const trimmedQuery = activeQuery.trim();

        if (onSearchSubmit) {
            onSearchSubmit(trimmedQuery);
            return;
        }

        if (!trimmedQuery) {
            if (location.pathname === '/search') {
                Navigate('/search');
                return;
            }

            setSearchOpen(defaultSearchOpen ? true : false);
            return;
        }

        Navigate(`/search?q=${encodeURIComponent(trimmedQuery)}&tab=all`);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        submitSearch(searchValue !== undefined ? searchValue : searchQuery);
    };

    const handleSearchButtonClick = () => {
        if (!searchOpen) {
            setSearchOpen(true);
            return;
        }

        submitSearch(searchValue !== undefined ? searchValue : searchQuery);
    };

    const handleCloseSearch = () => {
        if (onSearchChange) {
            onSearchChange('');
        } else {
            setSearchQuery('');
        }

        if (location.pathname === '/search') {
            Navigate('/search');
            return;
        }

        setSearchOpen(defaultSearchOpen ? true : false);
    };

    return (
        <>
            
            <header>
                <Navbar>
                    <Container>
                        <Navbar.Brand href={isMember ? "/feed" : "/"}>{theme === 'dark' ? <img src={Logo} alt='nav logo' id='mainNavLogo'></img> : <img src={LogoLight} alt='nav logo' id='mainNavLogo'></img>}</Navbar.Brand>
                        <div id='navIcons'>
                            <div className={`navSearchShell ${searchOpen ? 'navSearchShell--open' : ''}`}>
                                {searchOpen ? (
                                    <Form className='navSearchForm' onSubmit={handleSearchSubmit}>
                                        <Form.Control
                                            id='navSearch'
                                            type='text'
                                            placeholder={searchPlaceholder || 'Search posts, people, communities, opportunities...'}
                                            value={searchValue !== undefined ? searchValue : searchQuery}
                                            onChange={(event) => {
                                                if (onSearchChange) {
                                                    onSearchChange(event.target.value);
                                                } else {
                                                    setSearchQuery(event.target.value);
                                                }
                                            }}
                                            autoFocus={location.pathname !== '/search' && !defaultSearchOpen}
                                        />
                                    </Form>
                                ) : null}
                                <button type='button' className='navIconButton' aria-label='Search' onClick={handleSearchButtonClick}>
                                    <Search size={30} className='navIcon' id='searchIcon' />
                                </button>
                                {searchOpen ? (
                                    <button type='button' className='navIconButton navIconButton--dismiss' aria-label='Close search' onClick={handleCloseSearch}>
                                        <X size={24} className='navIcon navIcon--dismiss' />
                                    </button>
                                ) : null}
                            </div>
                            {isMember && (
                            <>
                            <OverlayTrigger
                                    trigger="click"
                                    key="notifications"
                                    placement='bottom'
                                    overlay={
                                        <Popover id={'popover-notifications'}>
                                            <Popover.Header as='h3'>Notifications</Popover.Header>
                                            <Popover.Body>
                                                <ListGroup variant='flush'>
                                                    {notifications && notifications.length > 0 ? (
                                                        notifications.map(n => (
                                                            <ListGroup.Item key={n.id} action>
                                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                                    <div>
                                                                        <strong>{n.type ? (n.type === 'notice' && n.message === 'Connection accepted' ? 'Connection' : n.type.charAt(0).toUpperCase() + n.type.slice(1)) : 'Notice'}</strong>
                                                                        <div>{n.message || (n.type === 'like' ? 'Someone liked your post' : n.type === 'repost' ? 'Someone reposted your post' : n.type === 'connection' ? 'New connection request' : '')}</div>
                                                                        {n.type === 'connection' && n.connectionId && (
                                                                            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                                                                                <Button variant="primary" size="sm" onClick={() => handleAcceptConnection(n)}>Accept</Button>
                                                                                <Button variant="secondary" size="sm" onClick={() => handleRejectConnection(n)}>Decline</Button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <small className='text-muted'>{n.createdAt && n.createdAt.toDate ? n.createdAt.toDate().toLocaleString() : ''}</small>
                                                                </div>
                                                            </ListGroup.Item>
                                                        ))
                                                    ) : (
                                                        <div className='text-center'>No notifications</div>
                                                    )}
                                                </ListGroup>
                                            </Popover.Body>
                                        </Popover>
                                    }
                                >
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <Bell  size={30} className='navIcon' id='notifications' onClick={handleNotificationsClick} />
                                        {unseenCount > 0 && (
                                            <Badge bg='danger' style={{ position: 'absolute', top: -4, right: -4, fontSize: '0.65rem' }}>{unseenCount}</Badge>
                                        )}
                                    </div>
                                </OverlayTrigger>
                            <Nav.Link href="/locate">
                                <div className={`navLink-wrapper ${location.pathname === '/locate' ? 'active' : ''}`}>
                                    <MapPin className={`navIcon ${location.pathname === '/locate' ? 'navIcon--active' : ''}`} size={30} alt='Locate' />
                                </div>
                            </Nav.Link>
                            <Nav.Link href="/communities">
                                <div className={`navLink-wrapper ${location.pathname.startsWith('/communities') ? 'active' : ''}`}>
                                    <Users className={`navIcon ${location.pathname.startsWith('/communities') ? 'navIcon--active' : ''}`} size={30} alt='Communities' />
                                </div>
                            </Nav.Link>
                            </>
                            )}
                            <Nav.Link href="/opportunities">
                                <div className={`navLink-wrapper ${location.pathname === '/opportunities' || location.pathname === '/' ? 'active' : ''}`}>
                                    <Sparkles className={`navIcon ${location.pathname === '/opportunities' || location.pathname === '/' ? 'navIcon--active' : ''}`} size={30} alt='Opportunities' />
                                </div>
                            </Nav.Link>
                            {isMember ? (
                            <>
                            <Nav.Link href="/inbox">
                                <div className={`navLink-wrapper ${location.pathname === '/inbox' ? 'active' : ''}`}>
                                    <Inbox className={`navIcon ${location.pathname === '/inbox' ? 'navIcon--active' : ''}`} size={30} alt='Inbox' />
                                </div>
                            </Nav.Link>
                            
                            <Dropdown align="end" className="nav-item">
                                <Dropdown.Toggle as="div" bsPrefix="p-0">
                                    <div className={`navLink-wrapper ${location.pathname === '/profile' ? 'active' : ''}`} style={{ cursor: 'pointer' }}>
                                        {profilePhoto ? (
                                            <Image src={profilePhoto} className={`navIcon ${location.pathname === '/profile' ? 'navIcon--active' : ''}`} id='profilePhotoNav'/>
                                        ) : (
                                            <CircleUserRound size={30} className={`navIcon ${location.pathname === '/profile' ? 'navIcon--active' : ''}`} />
                                        )}
                                    </div>
                                </Dropdown.Toggle>
                                <Dropdown.Menu style={{ backgroundColor: 'var(--popover-bg)', borderColor: 'var(--border-color)', borderRadius: '12px', minWidth: '220px', marginTop: '10px' }} className="shadow-lg profile-dropdown-menu">
                                    <Dropdown.Header style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem' }}>{username}</Dropdown.Header>
                                    <Dropdown.Divider style={{ borderColor: 'var(--border-color)' }} />
                                    {isAdmin && (
                                        <Dropdown.Item 
                                            onClick={() => Navigate('/system-admin')} 
                                            style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}
                                        >
                                            <Shield size={16} className="text-danger" /> Admin Panel
                                            <Badge bg="danger" style={{ fontSize: '0.65rem', marginLeft: 'auto' }}>ADMIN</Badge>
                                        </Dropdown.Item>
                                    )}
                                    {accountType === 'business' && (
                                        <Dropdown.Item onClick={() => Navigate('/business/dashboard')} style={{ color: 'var(--text-primary)' }}>Business Dashboard</Dropdown.Item>
                                    )}
                                    <Dropdown.Item onClick={()=> Navigate('/profile')} style={{ color: 'var(--text-primary)' }}>Profile</Dropdown.Item>
                                    <Dropdown.Item onClick={()=> Navigate('/settings')} style={{ color: 'var(--text-primary)' }}>Account Settings</Dropdown.Item>
                                    <Dropdown.Item onClick={() => signOutUser()} style={{ color: 'var(--text-primary)' }}>Sign out</Dropdown.Item>
                                    <Dropdown.Divider style={{ borderColor: 'var(--border-color)' }} />
                                    <Dropdown.Item onClick={handleThemeToggle} style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {theme === 'dark' ? <><Sun size={16} /> Light Mode</> : <><Moon size={16} /> Dark Mode</>}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                            </>
                            ) : (
                            // Visitors can browse public pages (Opportunities); everything else needs an account.
                            <div className="navGuestActions">
                                <Button size="sm" className="btn-custom btn-custom-primary" onClick={() => Navigate('/login', { state: { from: location } })}>Sign in</Button>
                                <Button size="sm" className="btn-custom btn-custom-secondary" onClick={() => Navigate('/createAccount')}>Join</Button>
                            </div>
                            )}
                        </div>
                    </Container>
                </Navbar>
            </header>
            <ToastContainer />
        </>
    );
}

export default AppHeader;